/**
 * User Menu
 *
 * Displays user info and auth actions in the header.
 * Shows sign in button when logged out, user menu when logged in.
 */

import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { User, LogOut, RefreshCw, Cloud, CloudOff, Check, Download, Upload } from 'lucide-react';
import { isSupabaseConfigured } from '@/lib/supabase';
import { toast } from 'sonner';
import { exportData, importData, validateExportFile } from '@/utils/importExport';
import { useRef, useState } from 'react';

export function UserMenu() {
  const { user, isAuthenticated, signOut, loading, syncStatus, manualSync } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);

  // Don't show anything if Supabase is not configured
  if (!isSupabaseConfigured) {
    return null;
  }

  // Show loading state
  if (loading) {
    return (
      <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
    );
  }

  // Show sign in button when not authenticated
  if (!isAuthenticated || !user) {
    return (
      <Link to="/auth">
        <Button variant="default" size="sm" className="gap-2">
          <User className="h-4 w-4" />
          Sign In
        </Button>
      </Link>
    );
  }

  // Show user menu when authenticated
  const displayName = user.user_metadata?.display_name || user.email?.split('@')[0] || 'User';
  const email = user.email;

  const handleExport = async () => {
    try {
      await exportData();
      toast.success('Data exported!', {
        description: 'Your backup file has been downloaded.',
      });
    } catch (error) {
      toast.error('Export failed', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);

    try {
      // Validate file first
      const validation = await validateExportFile(file);
      if (!validation.valid) {
        throw new Error(validation.error || 'Invalid file');
      }

      // Import with merge strategy
      const result = await importData(file, 'merge');

      toast.success('Data imported!', {
        description: `Imported ${result.stats.configs} quizzes, ${result.stats.sessions} sessions, ${result.stats.knowledge} knowledge records`,
      });

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      toast.error('Import failed', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-xs text-primary-foreground font-semibold">
            {displayName.charAt(0).toUpperCase()}
          </div>
          <span className="hidden sm:inline">{displayName}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium">{displayName}</p>
            {email && (
              <p className="text-xs text-muted-foreground">{email}</p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Sync Status */}
        <div className="px-2 py-1.5">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {syncStatus.isSyncing ? (
              <>
                <RefreshCw className="h-3 w-3 animate-spin" />
                <span>Syncing...</span>
              </>
            ) : syncStatus.error ? (
              <>
                <CloudOff className="h-3 w-3 text-destructive" />
                <span className="text-destructive">Sync error</span>
              </>
            ) : syncStatus.lastSyncAt ? (
              <>
                <Check className="h-3 w-3 text-green-600" />
                <span>
                  Synced {new Date(syncStatus.lastSyncAt).toLocaleTimeString()}
                </span>
              </>
            ) : (
              <>
                <Cloud className="h-3 w-3" />
                <span>Ready to sync</span>
              </>
            )}
          </div>
        </div>

        <DropdownMenuItem
          onClick={async () => {
            toast.promise(manualSync(), {
              loading: 'Syncing...',
              success: 'Data synced successfully!',
              error: 'Sync failed. Please try again.',
            });
          }}
          disabled={syncStatus.isSyncing}
          className="cursor-pointer"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${syncStatus.isSyncing ? 'animate-spin' : ''}`} />
          {syncStatus.isSyncing ? 'Syncing...' : 'Sync Now'}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Import/Export */}
        <DropdownMenuItem onClick={handleExport} className="cursor-pointer">
          <Download className="mr-2 h-4 w-4" />
          Export Data
        </DropdownMenuItem>

        <DropdownMenuItem onClick={handleImportClick} disabled={importing} className="cursor-pointer">
          <Upload className="mr-2 h-4 w-4" />
          {importing ? 'Importing...' : 'Import Data'}
        </DropdownMenuItem>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleImportFile}
          style={{ display: 'none' }}
        />

        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/profile" className="cursor-pointer">
            <User className="mr-2 h-4 w-4" />
            Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={signOut}
          className="cursor-pointer text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
